import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, X, FlaskConical } from "lucide-react";
import {
  PROFILE_KINDS, PROFILE_KIND_LABELS, VALIDATORS, EMPTY_FACTORIES,
} from "@/lib/profiles/schemas";
import {
  getLatest, createProfile, updateProfile,
} from "@/lib/profiles/storage";

import WellProfileForm   from "./forms/WellProfileForm";
import BHAProfileForm    from "./forms/BHAProfileForm";
import CasingProfileForm from "./forms/CasingProfileForm";
import MudProfileForm    from "./forms/MudProfileForm";
import PumpProfileForm   from "./forms/PumpProfileForm";
import OpStateProfileForm from "./forms/OperationalStateProfileForm";
import CreateKillSheetFromOpStateModal from "@/components/CreateKillSheetFromOpStateModal";

const FORMS = {
  well:    WellProfileForm,
  bha:     BHAProfileForm,
  casing:  CasingProfileForm,
  mud:     MudProfileForm,
  pump:    PumpProfileForm,
  opstate: OpStateProfileForm,
};

// P0 fix (Phase 3) — this form is a duplicate data-entry surface for data
// the canonical editors already capture. No longer discoverable from main
// navigation or any canonical entry point (see Home.jsx, CurrentOperationsCard,
// WellProfileEditor.jsx); still reachable directly and from the standalone
// Kill Sheet workflow. Per-kind notice naming the canonical editor below.
const CANONICAL_EDITOR = {
  well:    { label: "Well Profile",       to: "/wells" },
  bha:     { label: "BHA (inside a Well Profile)",           to: "/wells" },
  casing:  { label: "Casing Program (inside a Well Profile)", to: "/wells" },
  mud:     { label: "Mud Program (inside a Well Profile)",    to: "/wells" },
  pump:    { label: "Rig Data Sheet",     to: "/rig-sheets" },
  opstate: { label: "Rig Engineering (Session)" },
};

/**
 * ProfileEditor — shared wrapper for create / edit of any profile kind.
 * Route: /profiles/:kind/new  → create new
 *        /profiles/:kind/:id  → edit existing latest version
 *
 * Each kind delegates its body to a kind-specific Form component which is a
 * pure controlled component (props: value, onChange) so the wrapper owns
 * persistence + navigation uniformly.
 */
export default function ProfileEditor() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const valid = PROFILE_KINDS.includes(kind);
  const isNew = !id || id === "new";

  const [draft, setDraft] = useState(() => EMPTY_FACTORIES[kind] ? EMPTY_FACTORIES[kind]() : {});
  const [errors, setErrors] = useState([]);
  const [loaded, setLoaded] = useState(isNew);
  const [opstateRow, setOpstateRow] = useState(null);
  const [createKsOpen, setCreateKsOpen] = useState(false);
  const errorRef = useRef(null);
  // Id of a record created during THIS mount. Save no longer navigates, so the
  // route param stays "new" and can no longer be what tells the next Save that
  // the record already exists. Without this, a second Save would create a
  // second record instead of a new version of the first.
  const [createdId, setCreatedId] = useState(null);
  // Success confirmation rendered in place, replacing the sessionStorage
  // handoff that only the list page could display.
  const [saveFlash, setSaveFlash] = useState(null); // { label, version, wasNew }

  // The record this editor is bound to: the route id when editing, otherwise
  // whatever was just created here. Every save decision routes on this.
  const routeId    = isNew ? null : id;
  const effectiveId = routeId || createdId;
  const isExisting  = !!effectiveId;

  // A1 — editing after a failed Save clears the stale error list. setErrors is
  // otherwise only ever called inside _doSave, so without this the panel keeps
  // naming fields the operator has already corrected. The success flash is
  // cleared for the same reason: a stale "saved" above unsaved edits misleads.
  const handleDraftChange = (next) => {
    if (errors.length > 0) setErrors([]);
    if (saveFlash) setSaveFlash(null);
    setDraft(next);
  };

  // Auto-scroll the error banner into view whenever new validation errors
  // surface. Required UX: a user with the Save button below the fold would
  // otherwise see no visible change after clicking Save.
  useEffect(() => {
    if (errors.length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  useEffect(() => {
    if (!valid || isNew) return;
    const row = getLatest(kind, id);
    if (row) {
      setDraft(row.payload);
      if (kind === "opstate") setOpstateRow(row);
    }
    setLoaded(true);
  }, [kind, id, isNew, valid]);

  if (!valid) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-[var(--rs-text-2)]">Unknown profile kind: <code>{kind}</code></p>
        <Link to="/profiles" className="text-[var(--rs-amber)] underline text-[12px]">← Back to Profile Hub</Link>
      </div>
    );
  }
  if (!loaded) {
    return <div className="max-w-3xl mx-auto px-4 py-6 text-[var(--rs-text-3)]">Loading…</div>;
  }

  const Form = FORMS[kind];

  const _doSave = () => {
    const result = VALIDATORS[kind](draft);
    setErrors(result.errors || []);
    if (!result.ok) return null;
    try {
      const wasNew = !effectiveId;
      const row = effectiveId
        ? updateProfile(kind, effectiveId, draft)
        : createProfile(kind, draft);
      // Bind this mount to the new record BEFORE anything else can re-enter
      // _doSave, so every subsequent save updates it instead of creating.
      if (wasNew) setCreatedId(row.id);
      // Keep the opstate row current so the Create Kill Sheet action becomes
      // available immediately after the first save, without a reload.
      if (kind === "opstate") setOpstateRow(row);
      setSaveFlash({
        label: row.label || draft.label || "(unnamed)",
        version: row.version,
        wasNew,
      });
      return row;
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed"]);
      return null;
    }
  };

  const handleSave = () => {
    // A2 — no navigation. Saving used to leave for /profiles/{kind}, which
    // removed the page holding "Next → Rig Engineering" and so stranded it.
    // The confirmation is now rendered in place instead (see saveFlash).
    _doSave();
  };

  // Master Correction Pass, Defect 9 — Rig Operations is setup stage 3 of 4;
  // "Next" advances to stage 4 (Rig Engineering), which is the existing
  // Session Switcher, not a page of its own — mirrors Home's own step-4
  // action exactly, no fifth stage invented.
  const handleSaveAndNext = () => {
    if (_doSave()) window.dispatchEvent(new CustomEvent("rce:open-session-switcher"));
  };

  return (
    <div data-testid={`profile-editor-${kind}`} className="max-w-3xl mx-auto px-4 py-6">
      <Link
        to={`/profiles/${kind}`}
        data-testid="profile-editor-back"
        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> {PROFILE_KIND_LABELS[kind]}
      </Link>

      <header className="flex items-baseline justify-between gap-2 mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-1">
            {isExisting ? "Edit" : "New"}
          </div>
          <h1 className="text-[20px] font-semibold tracking-tight text-[var(--rs-text-1)]">
            {PROFILE_KIND_LABELS[kind].replace(/ Profiles$/, " Profile")}
          </h1>
        </div>
        {/* "Create Kill Sheet" moved from this top-right position into the
            bottom action row (Kill Sheet workflow correction, ticket 255586):
            Save new version | Next → Rig Engineering | Create Kill Sheet | Cancel */}
      </header>

      {CANONICAL_EDITOR[kind] && (
        <div
          data-testid={`profile-editor-canonical-notice-${kind}`}
          className="card-rs p-3 mb-3 text-[11.5px] text-[var(--rs-text-2)] leading-snug"
          style={{ borderColor: "rgba(120,200,255,0.35)", background: "rgba(120,200,255,0.05)" }}
        >
          This data now lives in the canonical{" "}
          {CANONICAL_EDITOR[kind].to ? (
            <Link to={CANONICAL_EDITOR[kind].to} className="underline" style={{ color: "#78c8ff" }}>
              {CANONICAL_EDITOR[kind].label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("rce:open-session-switcher"))}
              className="underline"
              style={{ color: "#78c8ff" }}
            >
              {CANONICAL_EDITOR[kind].label}
            </button>
          )}
          . This legacy form is kept only for the standalone Kill Sheet
          workflow — edits here do not sync there.
        </div>
      )}

      {errors.length > 0 && (
        <div
          ref={errorRef}
          data-testid="profile-editor-errors"
          className="card-rs p-3 mb-3"
          style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}
        >
          <div className="text-[11px] font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1">
            Cannot save — please fix:
          </div>
          <ul className="list-disc list-inside text-[11.5px] text-[var(--rs-text-1)]">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {saveFlash && (
        // Operational State, defect #8 (phone-width confirmation placement):
        // at phone width this top-of-form position sits well above the fold
        // where the user is working (bottom action area), so it's hidden
        // there and shown instead next to Save/Next below. Unchanged for
        // every other kind, and unchanged for opstate at sm+ width.
        <div
          data-testid={`profile-editor-flash-${kind}`}
          role="status"
          className={`card-rs p-3 mb-3 ${kind === "opstate" ? "hidden sm:block" : ""}`}
          style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
               style={{ color: "var(--rs-amber)" }}>
            {saveFlash.wasNew ? "Profile saved." : "Profile updated."}
          </div>
          <div className="text-[11.5px] text-[var(--rs-text-2)]">
            {saveFlash.label}{saveFlash.wasNew ? "" : ` · v${saveFlash.version}`}
          </div>
        </div>
      )}

      <div className="card-rs p-4">
        <Form value={draft} onChange={handleDraftChange} />
      </div>

      {kind === "opstate" && saveFlash && (
        // Phone-width counterpart of the top flash above: same content,
        // shown only below sm so the confirmation is visible in the bottom
        // action area the user is already looking at. Save still leaves the
        // user in place and Next → Rig Engineering is untouched below.
        <div
          data-testid={`profile-editor-flash-bottom-${kind}`}
          role="status"
          className="sm:hidden w-full card-rs p-3 mb-2"
          style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
               style={{ color: "var(--rs-amber)" }}>
            {saveFlash.wasNew ? "Profile saved." : "Profile updated."}
          </div>
          <div className="text-[11.5px] text-[var(--rs-text-2)]">
            {saveFlash.label}{saveFlash.wasNew ? "" : ` · v${saveFlash.version}`}
          </div>
        </div>
      )}

      <div
        className="flex items-center gap-2 mt-4 flex-wrap"
        // Pump-only clearance fix: the Pump form's per-row height (geometry
        // fields + calibration block) can push this bar low enough that the
        // page's natural scroll-to-bottom position leaves it underneath the
        // fixed bottom nav (58px content + border + safe-area inset). This
        // reserves the same clearance already proven in RigSheetEditor's
        // save bar so Save/Cancel are never blocked. Scoped to kind==='pump'
        // only, per the approved correction — no other editor is touched.
        style={kind === "pump" ? { marginBottom: "calc(83px + env(safe-area-inset-bottom, 0px))" } : undefined}
      >
        <button
          type="button"
          data-testid="profile-editor-save"
          onClick={handleSave}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
          style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E", minHeight: 40 }}
        >
          <Save className="w-4 h-4" /> {isExisting ? "Save new version" : "Save profile"}
        </button>
        {kind === "opstate" && (
          <button
            type="button"
            data-testid="profile-editor-next"
            onClick={handleSaveAndNext}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
            style={{ background: "transparent", color: "var(--rs-amber)", border: "1px solid var(--rs-amber)", minHeight: 40 }}
          >
            Next → Rig Engineering
          </button>
        )}
        {kind === "opstate" && (
          // Kill Sheet workflow correction (ticket 255586): Create Kill Sheet
          // sits in the bottom action row and is enabled only once this
          // Operational State has been saved, so the Kill Sheet is created
          // from the latest saved version (opstateRow is refreshed on save).
          <button
            type="button"
            data-testid="opstate-create-killsheet-btn"
            disabled={!(isExisting && opstateRow)}
            title={isExisting && opstateRow ? "Create a Kill Sheet from the latest saved version" : "Save the Operational State first"}
            onClick={() => { if (isExisting && opstateRow) setCreateKsOpen(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-40"
            style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}
          >
            <FlaskConical className="w-4 h-4" /> Create Kill Sheet
          </button>
        )}
        <button
          type="button"
          data-testid="profile-editor-cancel"
          onClick={() => nav(`/profiles/${kind}`)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
          style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}
        >
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>

      {isExisting && (
        <p className="text-[10.5px] text-[var(--rs-text-3)] mt-3 leading-snug">
          Saving creates a new version. The previous version stays in storage so
          historical kill sheets that referenced it keep resolving correctly.
        </p>
      )}

      {kind === "opstate" && opstateRow && (
        <CreateKillSheetFromOpStateModal
          open={createKsOpen}
          opstateRow={opstateRow}
          onClose={() => setCreateKsOpen(false)}
        />
      )}
    </div>
  );
}
